<template>
  <div>
    <progressLinear
      v-if="!hasPause"
      :color="`${ qualifyingTimeIsAborted ? 'grey' : 'light-blue' }`"
      :value="getTimePercentage(education.qualificationTime.fromDate, education.qualificationTime.toDate, education.qualificationTime)"
    />

    <template v-if="qualifyingTimeIsAborted">
      <p class="ligth-grey" v-if="education.qualificationTime.isCancelled"
        v-html="
          $t('qualifying.qualifying-period-cancelled', {
            proffession: getProfessionNameById(education.professionId),
            start: $options.filters.date(education.qualificationTime.fromDate)
          })
        "
      ></p>

      <p class="ligth-grey" v-if="education.qualificationTime.doAbort"
        v-html="
          $t('qualifying.qualifying-period-stopped', {
            proffession: getProfessionNameById(education.professionId),
            start: $options.filters.date(qtToDate(education.qualificationTime))
          })
        "
      ></p>
    </template>

    <MessageBox class="mb-4 mt-4" v-if="hasPause">
      <p>{{ getActiveQualificationTimePausesText }}</p>
    </MessageBox>

    <p v-html="qualifyingPeriodText" v-if="!hasPause && !qualifyingTimeIsAborted"></p>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {Utilities} from '@/components/mixins'
import progressLinear from '@/components/Common/ProgressLinear'
import get from 'lodash/get'
import Sugar from 'sugar'
import orderBy from 'lodash/orderBy'
import MessageBox from '@/components/Common/MessageBox.vue'

export default {
  name: 'QualifyingTimeItem',

  components: {
    progressLinear,
    MessageBox
  },

  mixins: [Utilities],
  props: {
    education: Object,

    instructors: {
      type: Array,
      default: () => ([])
    }
  },
  computed: {
    ...mapGetters('basic', ['getProfessionNameById']),

    qualifyingPeriodText(){
      const textLabel = `qualifying.qualifying-period${this.hasPause ? '-paused': ''}`
      
      const currentApprenticeshipDescription = this.$t(textLabel, {
        proffession: this.getProfessionNameById(this.education.professionId),
        start: this.$options.filters.date(this.education.qualificationTime.fromDate),
        end: this.$options.filters.date(this.calculatedEndDate),
        instructors: get(this, 'apprenticeData.instructors', ''),
        companyName: get(this, 'apprenticeData.companyName', ''),
        length: this.displayQualifyingTime().length,
        completed: this.displayQualifyingTime().completed,
        remaining: this.displayQualifyingTime().remaining
      })

      if ( !this.previousEmployments.length ) return currentApprenticeshipDescription

      return `${ currentApprenticeshipDescription } <br /><br />
        ${ this.$t('qualifying.previous-employment', {
          employments: this.previousEmployments
        }) }
      `
    },

    apprenticeData() {
      if ( !get(this, 'education.apprenticeships', []).length ) {
        return {
          instructors: get(this, 'instructors', []).filter(Boolean).map(({ employed }) => `${ get(employed, 'firstName', '') } ${ get(employed, 'lastName', '') }`).join(', '),
          companyName: get(this, 'companyName', '')
        }
      }

      return get(this, 'education.apprenticeships', []).map(({ educationId, apprenticeshipInstructors, company }) => {
        if ( get(this, 'education.id', null) !== educationId ) return ''
        return {
          instructors: orderBy(get(this, 'education.apprenticeships', []), ['createdAt'], ['desc'])[0]
          .apprenticeshipInstructors.filter(({ instructor }) => instructor?.employed?.firstName && instructor?.employed?.lastName)
            .map(({ instructor }) => `${ get(instructor, 'employed.firstName', '') } ${ get(instructor, 'employed.lastName', '') }`).join(', '),
          companyName: orderBy(get(this, 'education.apprenticeships', []), ['createdAt'], ['desc'])[0]?.company.name || ''
        }
      })[0]
    },

    previousEmployments() {
      const employments = this.education.apprentice?.apprenticeships || this.education?.apprenticeships
      return orderBy(employments, ['toDate'], ['desc'])
      ?.filter(({ manuallyEndedDate }) => manuallyEndedDate)
      ?.map(employment => 
      `<b>${ employment.company?.name }</b> (${ Sugar.Date(employment.fromDate).format('{yyyy}-{MM}-{dd}').raw } - ${ Sugar.Date(employment.toDate).format('{yyyy}-{MM}-{dd}').raw })`).join(', ') 
    },

    calculatedEndDate() {
      return get(this, 'education.qualificationTime.toDate', 0)
    },

    getActiveQualificationTimePausesText() {
      const activePause = get(this, 'education.qualificationTimePauses', []).find(qt => {
        if(!qt){
          return true
        }
        return Sugar.Date(qt.startDate).isBefore().raw && Sugar.Date(qt.endDate).isAfter().raw
      })

      if ( activePause == null ) return null

      if(!!Number(activePause.employmentRate)){
        const employmentRate = activePause.employmentRate * 100.0 + "%"
        return this.$t("qualifying.employment-rate-explanation", { employmentRate })
      }

      return this.$t("qualifying.book-pause", {
        startDate: Sugar.Date(new Date(get(activePause, 'startDate', null))).format("{yyyy}-{MM}-{dd}"),
        endDate: Sugar.Date(new Date(get(activePause, 'endDate', null))).format("{yyyy}-{MM}-{dd}")
      })
    },

    hasPause() {
      return get(this, 'education.qualificationTimePauses', []).some(qt => {
        if( !qt.approvedBySecondaryId ) return false
        if( !qt.endDate ) return true
        return Sugar.Date(qt.startDate).isBefore().raw && Sugar.Date(qt.endDate).isAfter().raw
      })
    },

    qualifyingTimeIsAborted() {
      if ( !get(this, 'education.qualificationTime', null) ) return false
      const { doAbort } = get(this, 'education.qualificationTime', null)
      return doAbort
    },

    companyName() {
      let apprenticeships = get(this, 'education.apprentice.apprenticeships', [])
      if ( !apprenticeships.length ) apprenticeships = get(this, 'education.apprenticeships', [])
      if ( !apprenticeships.length ) return
      
      return apprenticeships?.find(apprenticeship => apprenticeship?.companyId?.toString() === this.$route.params.id?.toString())
        ?.company?.name
    }
  },

  methods: {
    getTimePercentage(start, end, qt = null) {
      let nowDate = new Date()
      
      if ( qt.doAbort ) {
        nowDate = new Date(qt.isAbortedDate)
      }

      const startDate = new Date(start)
      const endDate = new Date(end)
      const totalTime = endDate - startDate
      const passedTime = nowDate - startDate

      return this.getPercentage(passedTime, totalTime)
    },

    qtToDate(qt = null) {
      return qt.doAbort ? get(qt, 'isAbortedDate', null) : get(qt, 'toDate', null)
    },

    displayQualifyingTime() {
      const qualifyingTime = this.education?.qualificationTime
      const calculatedTotalLength = Sugar.Date.range(qualifyingTime.fromDate, (this.calculatedEndDate || qualifyingTime.toDate)).months()
      const qualifyingLength = this.education?.qualificationTimeLength || calculatedTotalLength

      const isWithinRange = Sugar.Date.range(qualifyingTime.fromDate, qualifyingTime.toDate).contains(new Date())
      const remaining = isWithinRange ? Sugar.Date.range(new Date(), qualifyingTime.toDate).months() : 0

      return {
        length: `${qualifyingLength} ${(this.$t(`labels.${qualifyingLength > 1 ? 'months' : 'month'}` ))}`,
        completed: `${qualifyingLength - remaining} ${(this.$t(`labels.${qualifyingLength - remaining > 1 ? 'months' : 'month'}` ))}` ,
        remaining: `${remaining} ${(this.$t(`labels.${!remaining || remaining > 1 ? 'months' : 'month'}` ))}`,
      }
    }
  }
}
</script>

<style>
</style>